import { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react"

// Firebase utilities
import { db } from "../../../../utils/firebase"

// Providers
import { AlertsContext } from "../../../../utils/providers/alerts"

// Algolia (same instance + indexes the New Quote / Build Quote search uses)
import algoliasearch from "algoliasearch/lite"
import { InstantSearch, connectSearchBox, connectHits, connectStateResults } from "react-instantsearch-dom"

// Icons
import { Search as SearchGlyph, Plus } from "lucide-react"

// UI components
import Input from "../../../ui/inputs/input"
import Badge from "../../../ui/badge/badge"
import Select from "../../../ui/select/select"
import Checkbox from "../../../ui/checkbox/checkbox"
import { TimesIcon } from "../../../../utils/svgs"

import "./quoting.scss"

// ---------------------------------------------------------------------------
// Per-course transfer options. The value is the stored key, the label is what
// the agent sees. "Resort shuttle" doubles as the hotel-shuttle flag — a course
// set to it is one the resort runs a shuttle to (this replaced the old separate
// golf-shuttles checklist). Stored as `transfer_mode` (not `transfer_type`) to
// avoid clashing with the unrelated booking-level `transfer_type` field, which
// uses a different enum (airport / golf / airport_and_golf / nothing).
// ---------------------------------------------------------------------------
const TRANSFER_MODES = {
	resort_shuttle: "Resort shuttle",
	external: "External transfer",
	self_drive: "Self-drive",
}
const DEFAULT_TRANSFER_MODE = "resort_shuttle"

// Ensure every quotable-course entry carries the per-course flags. Existing
// values win; missing ones fall back to sensible defaults. Applied when seeding
// state and when adding a course so the stored shape is always consistent.
const normaliseCourse = (course) => ({
	included_in_package: false,
	transfer_mode: DEFAULT_TRANSFER_MODE,
	...course,
})

// ---------------------------------------------------------------------------
// Algolia picker — a search box + inline result list, mirroring the New Quote
// search. Hits already added (or the hotel itself) are filtered out so the same
// entity can't be picked twice. Connected widgets live at module scope and take
// their per-instance config (onPick / excludeIds / badge) as plain own-props,
// which react-instantsearch merges alongside the connected props.
// ---------------------------------------------------------------------------
const SearchInput = connectSearchBox(({ currentRefinement, refine, placeholder, onType }) => (
	<form noValidate role="search" onSubmit={(event) => event.preventDefault()}>
		<Input
			value={currentRefinement}
			icon={<SearchGlyph size={16} />}
			fullEvent={true}
			placeholder={placeholder}
			onChange={(event) => {
				refine(event.currentTarget.value)
				onType && onType()
			}}
		/>
	</form>
))

const PickerHits = connectHits(({ hits, excludeIds, onPick, badgeLabel, badgeType }) => {
	const visible = hits.filter((hit) => !excludeIds.includes(hit.objectID)).slice(0, 8)
	if (!visible.length) return null

	return (
		<div className="hq-result-group">
			{visible.map((hit) => (
				<div key={hit.objectID} className="hq-result" onClick={() => onPick(hit)}>
					<Badge label={badgeLabel} type={badgeType} />
					<p>{hit.title}</p>
					<Plus size={14} />
				</div>
			))}
		</div>
	)
})

// Only render hits once something has been typed (mirrors the New Quote search).
const PickerResults = connectStateResults(({ searchState, excludeIds, onPick, badgeLabel, badgeType }) =>
	searchState.query ? <PickerHits excludeIds={excludeIds} onPick={onPick} badgeLabel={badgeLabel} badgeType={badgeType} /> : null
)

function AlgoliaPicker({ searchClient, indexName, placeholder, badgeLabel, badgeType, excludeIds, onPick }) {
	const [open, setOpen] = useState(false)
	const wrapperRef = useRef(null)

	// Close the dropdown when clicking outside the picker.
	useEffect(() => {
		const handleClick = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false)
		}
		document.addEventListener("mousedown", handleClick)
		return () => document.removeEventListener("mousedown", handleClick)
	}, [])

	return (
		<div className="hq-search" ref={wrapperRef} onFocus={() => setOpen(true)} onKeyDown={(event) => event.key === "Escape" && setOpen(false)}>
			<InstantSearch searchClient={searchClient} indexName={indexName}>
				<SearchInput placeholder={placeholder} onType={() => setOpen(true)} />
				{open && (
					<div className="hq-results">
						<PickerResults excludeIds={excludeIds} onPick={onPick} badgeLabel={badgeLabel} badgeType={badgeType} />
					</div>
				)}
			</InstantSearch>
		</div>
	)
}

// ---------------------------------------------------------------------------
// The "Quoting" tab — curates the courses and companion hotels this hotel can
// be quoted alongside (decoupled from the connected `resort_ids` courses). Each
// quotable course also records whether it's bundled into the hotel's packages
// and how golfers get there (resort shuttle / external transfer / self-drive).
// Holidays only.
// ---------------------------------------------------------------------------
export default function HolidayQuoting({ holiday }) {
	const { pushAlert } = useContext(AlertsContext)

	// One Algolia client shared by both pickers.
	const searchClient = useMemo(() => algoliasearch("HY764WYKNV", "059a9cd79a33ec2b2822acf2ffdfd4c9"), [])

	// Curated quotable courses + companion hotels. Initialised from the streamed
	// doc once; we then update optimistically and persist, matching the Location
	// & Features pattern. Courses are normalised so older { id, title } entries
	// gain the per-course flags.
	const [courses, setCourses] = useState((holiday?.quoting_courses || []).map(normaliseCourse))
	const [hotels, setHotels] = useState(holiday?.quoting_hotels || [])

	// Merge a partial update into this hotel's document.
	const save = useCallback(
		async (partial) => {
			try {
				await db.doc(`CMS_holidays/${holiday.id}`).set(partial, { merge: true })
			} catch (error) {
				pushAlert({ type: "ERROR", title: "Could not save", body: error.message })
			}
		},
		[holiday.id, pushAlert]
	)

	// --- Quotable courses --------------------------------------------------
	const addCourse = (hit) => {
		if (courses.some((course) => course.id === hit.objectID)) return
		const next = [...courses, normaliseCourse({ id: hit.objectID, title: hit.title || "Course" })]
		setCourses(next)
		save({ quoting_courses: next })
	}

	const removeCourse = (courseId) => {
		const next = courses.filter((course) => course.id !== courseId)
		setCourses(next)
		save({ quoting_courses: next })
	}

	// Patch a single course's flags in place and persist the whole array.
	const updateCourse = (courseId, patch) => {
		const next = courses.map((course) => (course.id === courseId ? { ...course, ...patch } : course))
		setCourses(next)
		save({ quoting_courses: next })
	}

	const toggleIncluded = (course) => updateCourse(course.id, { included_in_package: !course.included_in_package })
	const setTransferMode = (courseId, mode) => updateCourse(courseId, { transfer_mode: mode })

	// --- Companion hotels (reciprocal) -------------------------------------
	// Linking B to A also writes A into B's list (and removing mirrors), so the
	// pairing stays in sync from either hotel. The far side is a read-modify-
	// write keyed on id so a title change never duplicates an entry. It throws on
	// failure so linkHotel can keep both sides in step.
	const writeCompanionLink = async (otherId, add) => {
		const ref = db.doc(`CMS_holidays/${otherId}`)
		const snapshot = await ref.get()
		const existing = snapshot.data()?.quoting_hotels || []

		const next = add
			? existing.some((entry) => entry.id === holiday.id)
				? existing
				: [...existing, { id: holiday.id, title: holiday.title || "" }]
			: existing.filter((entry) => entry.id !== holiday.id)

		await ref.set({ quoting_hotels: next }, { merge: true })
	}

	// Apply a companion change to this hotel and the far one. We update
	// optimistically for a snappy UI, but if the far-side write fails we roll the
	// local state and this doc back, so the two hotels can never end up linked one
	// way only.
	const linkHotel = async (next, otherId, add) => {
		const previous = hotels
		setHotels(next)
		save({ quoting_hotels: next })

		try {
			await writeCompanionLink(otherId, add)
		} catch (error) {
			setHotels(previous)
			save({ quoting_hotels: previous })
			pushAlert({ type: "ERROR", title: "Could not link the other hotel", body: error.message })
		}
	}

	const addHotel = (hit) => {
		if (hit.objectID === holiday.id || hotels.some((hotel) => hotel.id === hit.objectID)) return
		const next = [...hotels, { id: hit.objectID, title: hit.title || "Hotel" }]
		linkHotel(next, hit.objectID, true)
	}

	const removeHotel = (hotelId) => {
		const next = hotels.filter((hotel) => hotel.id !== hotelId)
		linkHotel(next, hotelId, false)
	}

	const courseExcludeIds = courses.map((course) => course.id)
	const hotelExcludeIds = [holiday.id, ...hotels.map((hotel) => hotel.id)]

	return (
		<div className="holiday-quoting">
			{/* ---------------------------------------------------------- */}
			{/* QUOTABLE COURSES                                             */}
			{/* ---------------------------------------------------------- */}
			<div className="hq-section">
				<h3>Quotable courses</h3>
				<p className="hq-hint">
					Search any course to quote alongside this hotel - these don't need to be connected courses. For each, set whether it's bundled into the hotel's packages and how golfers get there.
				</p>

				<AlgoliaPicker
					searchClient={searchClient}
					indexName="cms_courses"
					placeholder="Search courses…"
					badgeLabel="Course"
					badgeType="POSITIVE"
					excludeIds={courseExcludeIds}
					onPick={addCourse}
				/>

				{courses.length > 0 ? (
					<div className="hq-list">
						{courses.map((course) => (
							<div key={course.id} className="hq-item">
								<Badge label="Course" type="POSITIVE" />
								<span className="hq-item-title">{course.title}</span>

								<Checkbox
									className="hq-item-flag"
									label="In packages"
									checked={!!course.included_in_package}
									onClick={() => toggleIncluded(course)}
								/>

								<Select
									className="hq-item-transfer"
									options={TRANSFER_MODES}
									selected={course.transfer_mode || DEFAULT_TRANSFER_MODE}
									onSelect={({ option }) => setTransferMode(course.id, option)}
								/>

								<span className="hq-item-remove" onClick={() => removeCourse(course.id)} title="Remove">
									<TimesIcon />
								</span>
							</div>
						))}
					</div>
				) : (
					<p className="hq-empty">No quotable courses added yet.</p>
				)}
			</div>

			{/* ---------------------------------------------------------- */}
			{/* COMPANION HOTELS                                             */}
			{/* ---------------------------------------------------------- */}
			<div className="hq-section">
				<h3>Also quote these hotels</h3>
				<p className="hq-hint">If we're quoting this hotel, we can offer these alternatives too. Links are reciprocal - each hotel gets added to the other.</p>

				<AlgoliaPicker
					searchClient={searchClient}
					indexName="cms_holidays"
					placeholder="Search hotels…"
					badgeLabel="Hotel"
					badgeType="INFO"
					excludeIds={hotelExcludeIds}
					onPick={addHotel}
				/>

				{hotels.length > 0 ? (
					<div className="hq-list">
						{hotels.map((hotel) => (
							<div key={hotel.id} className="hq-item">
								<Badge label="Hotel" type="INFO" />
								<span className="hq-item-title">{hotel.title}</span>
								<span className="hq-item-remove" onClick={() => removeHotel(hotel.id)} title="Remove">
									<TimesIcon />
								</span>
							</div>
						))}
					</div>
				) : (
					<p className="hq-empty">No companion hotels added yet.</p>
				)}
			</div>
		</div>
	)
}
